import { makeAutoObservable } from 'mobx';

import cartStore from './cart-store';
import globalStore from './global-store';
import popupStore from './popup-store';

import { mapClientToApi, mapDeceasedDataToApi } from 'src/adapters';
import { doPost } from 'src/api';
import {
  ApiResponseStatus,
  ApiRoute,
  DEFAULT_PAYMENT_TAB,
  ErrorText,
  LOCALES,
  PaymentTab,
  PaymentTabLabel,
  PaymentType,
  PopupName,
  Regex,
} from 'src/constants';
import { getPaidOrderNumber, setPaidOrderNumber } from 'src/utils';
import type {
  ICommonCreateOrderResponse,
  IMeshulamCreateOrderResponse,
  IPaypalCreateOrderResponse,
  IStripeCreateOrderResponse,
} from 'src/interfaces';

class PaymentStore {
  activeTab: PaymentTab = DEFAULT_PAYMENT_TAB;
  isCardElementComplete: boolean = false;
  isCvcElementComplete: boolean = false;
  isExpiryElementComplete: boolean = false;
  isPaymentLoading: boolean = false;
  paidOrderNumber: string = getPaidOrderNumber();
  paymentFormError: string = '';

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setActiveTab(activeTab: PaymentTab) {
    this.activeTab = activeTab;
  }

  setPaymentLoading(isPaymentLoading: boolean) {
    this.isPaymentLoading = isPaymentLoading;
  }

  setPaymentFormError(error: string) {
    this.paymentFormError = error;
  }

  setPaidOrderNumber(orderNumber: string) {
    this.paidOrderNumber = orderNumber;
    setPaidOrderNumber(orderNumber);
  }

  clearForm() {
    this.isCardElementComplete = false;
    this.isCvcElementComplete = false;
    this.isExpiryElementComplete = false;
    this.isPaymentLoading = false;
    this.paymentFormError = '';
  }

  handleFormChange(error: string) {
    if (error) {
      this.setPaymentFormError(error);
    } else {
      this.setPaymentFormError('');
    }
  }

  handleCardElementChange(evt: any) {
    this.isCardElementComplete = evt.complete;
    this.handleFormChange(evt?.error?.message);
  }

  handleCvcElementChange(evt: any) {
    this.isCvcElementComplete = evt.complete;
    this.handleFormChange(evt?.error?.message);
  }

  handleExpiryElementChange(evt: any) {
    this.isExpiryElementComplete = evt.complete;
    this.handleFormChange(evt?.error?.message);
  }

  get isFormComplete() {
    return this.isCardElementComplete && this.isCvcElementComplete && this.isExpiryElementComplete;
  }

  get isPaypalSelected() {
    return this.activeTab === PaymentTab.PAYPAL;
  }

  get paymentsTabs() {
    return Object.entries(PaymentTab).map(
      ([name, value]) => ({
        name,
        label: PaymentTabLabel[name],
        onClick: () => {
          this.setActiveTab(value);
          this.setPaymentFormError('');
        },
      }),
      {}
    );
  }

  createOrder(paymentType: PaymentType.STRIPE): Promise<IStripeCreateOrderResponse>;
  createOrder(
    paymentType: PaymentType.MESHULAM,
    errorHandler?: () => void
  ): Promise<IMeshulamCreateOrderResponse>;
  createOrder(paymentType: PaymentType.PAYPAL): Promise<IPaypalCreateOrderResponse>;
  createOrder(paymentType: null): Promise<ICommonCreateOrderResponse>;
  async createOrder(paymentType: PaymentType | null, errorHandler?: () => void) {
    const paymentError = (errorText: string) => {
      popupStore.showPopup(PopupName.INFO, {
        title: LOCALES.PAYMENT_ERROR,
        text: errorText,
      });
      // if paymentType === null, payment step will be skipped
      if (paymentType) {
        this.setPaymentFormError(errorText);
      }
    };

    if (!cartStore.clientInfo) {
      paymentError(ErrorText.FILL_FORM);
      return {};
    }

    const { isNoSlug } = globalStore;

    const order = {
      ...mapClientToApi(cartStore.clientInfo),
      ...(isNoSlug && { cemetery: cartStore.clientInfo.cemetery }),
      ...(paymentType && { payment_type: paymentType }),
      deceased: isNoSlug ? mapDeceasedDataToApi(cartStore.clientInfo) : globalStore.deceasedId,
      items: Object.entries(cartStore.lots).map(([id, amount]) => ({ id, amount })),
      total: cartStore.totalSum,
    };

    this.setPaymentLoading(true);

    const ordersEndpoint = isNoSlug ? ApiRoute.ORDERS_UNLOCATED_DECEASED : ApiRoute.ORDERS;

    try {
      const response = await doPost(ordersEndpoint, order);
      return response.data;
    } catch (err: any) {
      if (err?.response?.status === ApiResponseStatus.CONFLICT) {
        paymentError(ErrorText.SUM_CONFLICT);
      } else {
        const text = Regex.PHONE_INVALID.test(err.response?.data?.error)
          ? LOCALES.PHONE_FORMAT
          : ErrorText.DEFAULT;
        paymentError(text);
      }

      this.setPaymentLoading(false);
      if (typeof errorHandler === 'function') {
        errorHandler();
      }
      return {};
    }
  }
}

export default new PaymentStore();
